import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Search, Calendar, User, ChevronLeft, ChevronRight } from 'lucide-react';
import { getBlogs } from '../api/blogApi';



const BlogCardSkeleton = () => (
  <div className="animate-pulse flex flex-col gap-4">
    <div className="w-full h-64 bg-gray-100 dark:bg-gray-800 rounded-[2rem]"></div>
    <div className="flex gap-2 items-center">
      <div className="w-20 h-2.5 bg-gray-100 dark:bg-gray-800 rounded-full"></div>
      <div className="w-1 h-1 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
      <div className="w-20 h-2.5 bg-gray-100 dark:bg-gray-800 rounded-full"></div>
    </div>
    <div className="w-full h-4 bg-gray-100 dark:bg-gray-800 rounded-full"></div>
    <div className="w-4/5 h-4 bg-gray-100 dark:bg-gray-800 rounded-full"></div>
    <div className="w-full h-3 bg-gray-100 dark:bg-gray-800 rounded-full"></div>
    <div className="w-2/3 h-3 bg-gray-100 dark:bg-gray-800 rounded-full"></div>
  </div>
);

const Blogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [categoryOptions, setCategoryOptions] = useState(['All']);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const LIMIT = 9;

  const categoryScrollRef = useRef(null);

  const scrollCategories = () => {
    if (categoryScrollRef.current) {
      categoryScrollRef.current.scrollBy({ left: 150, behavior: 'smooth' });
    }
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [selectedCategory]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await getBlogs({ status: 'Published', limit: 1000 });
        if (response.success) {
          setCategoryOptions(Array.from(new Set(['All', ...response.data.map(b => b.category).filter(Boolean)])));
        }
      } catch (err) {
        console.error('Error fetching blog categories:', err);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setLoading(true);
        const params = { status: 'Published', limit: LIMIT, page };
        if (selectedCategory !== 'All') {
          params.category = selectedCategory;
        }
        if (debouncedSearch.trim()) {
          params.search = debouncedSearch.trim();
        }
        const response = await getBlogs(params);
        if (response.success) {
          setBlogs(response.data);
          setTotal(response.total || response.data.length);
        }
      } catch (err) {
        console.error('Error fetching blogs:', err);
        setBlogs([]);
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, [page, selectedCategory, debouncedSearch]);

  const totalPages = Math.ceil(total / LIMIT);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'MAR 2025';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  // Keep categories stable even when the blog list is filtered by category.
  const dynamicCategories = categoryOptions;

  return (
    <div className="min-h-screen bg-[#fafaf8] dark:bg-gray-950">
      {/* ─── Hero / Page Header ─── */}
      <div className="bg-white dark:bg-gray-950 pt-6 pb-7">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-[1px] w-10 bg-amber-500"></div>
            <span className="text-amber-600 font-bold text-xs tracking-widest uppercase">The Bright Laptop Blog</span>
          </div>
          <p className="text-gray-500 dark:text-gray-400 text-lg mb-4 w-full">
            Buying guides, comparisons, and tips for refurbished buyers. Stay informed.
          </p>
        </div>
      </div>

      {/* ─── Category Filter Pills & Search ─── */}
      <div className="bg-white dark:bg-gray-900 sticky top-0 z-20 pb-4 pt-2 border-b border-gray-100 dark:border-gray-700 shadow-sm">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            
            {/* Left: TYPE label + Categories Container */}
            <div className="flex flex-col gap-2 min-w-0">
              <span className="text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest pl-1">Explore Categories</span>
              <div className="flex items-center gap-2">
                <div 
                  ref={categoryScrollRef}
                  className="flex items-center bg-slate-50 dark:bg-gray-800 rounded-xl p-1.5 overflow-x-auto w-max max-w-[calc(100vw-4rem)] md:max-w-xl [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] scroll-smooth"
                >
                  {dynamicCategories.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setSelectedCategory(cat)}
                      className={`flex-shrink-0 px-5 py-2 rounded-lg text-sm font-semibold transition-all ${
                        selectedCategory === cat
                          ? 'bg-amber-500 dark:bg-amber-600 text-white shadow-md'
                          : 'text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-gray-700'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
                {dynamicCategories.length > 5 && (
                  <button 
                    onClick={scrollCategories}
                    className="flex items-center justify-center w-8 h-8 bg-white dark:bg-gray-700 border border-slate-200 dark:border-gray-600 rounded-full text-slate-500 dark:text-slate-400 hover:text-amber-600 hover:border-amber-400 hover:shadow-md transition-all shadow-sm"
                  >
                    <ChevronRight size={18} />
                  </button>
                )}
              </div>
            </div>

            {/* Right: Search Bar */}
            <div className="relative w-full md:w-[320px] flex-shrink-0 pb-[2px]">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search articles..."
                className="w-full pl-11 pr-4 py-2.5 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-600 rounded-xl text-sm text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 transition-all shadow-sm"
              />
            </div>

          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 max-w-6xl py-16">
        {/* ─── Blog Grid ─── */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, i) => <BlogCardSkeleton key={i} />)}
          </div>
        ) : blogs.length === 0 ? (
          <div className="text-center py-32">
            <div className="text-6xl mb-6">📰</div>
            <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">No articles found</h3>
            <p className="text-gray-500 dark:text-gray-400">
              {debouncedSearch
                ? `No results for "${debouncedSearch}". Try a different search.`
                : 'No published blogs yet in this category.'}
            </p>
          </div>
        ) : (
          <>
            {/* Section label */}
            <div className="flex items-center gap-4 mb-8">
              <p className="text-sm font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Articles</p>
              <div className="flex-1 h-[1px] bg-gray-100 dark:bg-gray-700"></div>
              <p className="text-sm text-gray-400 dark:text-gray-500">{total} total</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogs.map((blog) => (
                <Link key={blog._id} to={`/blog/${blog.slug}`} className="group block">
                  <article className="flex flex-col h-full">
                    {/* Industrial Image Card */}
                    <div className="relative w-full aspect-[16/9] rounded-2xl overflow-hidden mb-4 bg-gray-100 flex-shrink-0">
                      <img
                        src={blog.featuredImage}
                        alt={blog.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    {/* Clean Text Content Below Image */}
                    <div className="px-1 flex flex-col flex-grow">
                      <span className="text-[11px] font-bold text-blue-600 uppercase tracking-widest mb-2 block">
                        {blog.category || 'Uncategorized'}
                      </span>
                      <h3 
                        className="text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100 leading-snug mb-3 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors line-clamp-2"
                        dangerouslySetInnerHTML={{ __html: blog.title }}
                      />
                      <p 
                        className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed line-clamp-2 mb-4 flex-grow"
                        dangerouslySetInnerHTML={{ __html: blog.excerpt }}
                      />
                      
                      {/* Meta Data */}
                      <div className="flex items-center gap-3 text-[11px] font-bold uppercase tracking-widest border-t border-gray-100 dark:border-gray-700 pt-3 mt-auto">
                        <span className="flex items-center gap-1 text-amber-600"><User size={12} />{blog.author || 'Bright Laptop'}</span>
                        <span className="flex items-center gap-1 text-amber-500"><Calendar size={12} />{formatDate(blog.publishedAt)}</span>
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>

            {/* ─── Pagination ─── */}
            {!debouncedSearch && totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 mt-16">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}
                  className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft size={18} />
                </button>
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i + 1)}
                    className={`w-10 h-10 flex items-center justify-center rounded-full text-sm font-bold transition-all ${
                      page === i + 1
                        ? 'bg-gray-900 dark:bg-blue-600 text-white shadow-md'
                        : 'border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                  className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Blogs;
